import React, { PropsWithChildren, ReactNode, useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  ICanDropOutcome,
  IDndFlexPrivateContext,
  IGridBorderMenuItemsFactory,
  IGridDropPosition,
  IGridPosition,
} from './types';
import {
  CollisionDescriptorSchema,
  getClosestCollision,
  GridPositionSchema,
} from './utils';
import { DndGridInternalContext } from './context';
import { INITIAL_CAN_DROP_OUTCOME } from './constants';
import { DndGridWrapper } from './DndGridWrapper';
import { DraggingGlobalStyle } from './DraggingGlobalStyle';
import { FloatingLayer } from '../FloatingManager';

interface IDndGridProps {
  onDrop: (source: IGridPosition, destination: IGridDropPosition) => void;
  canDrop: (
    source: IGridPosition,
    destination: IGridDropPosition,
  ) => ICanDropOutcome;
  disabled: boolean;
  overlay: ReactNode;
  borderMenuItemsFactory?: IGridBorderMenuItemsFactory;
}

/**
 * A Kubit-specific implementation of a Drag & Drop where items are displayed in a grid.
 *
 * The grid contains rows and every row has variable number of cells. User can rearrange it items in the same row, or
 * "pop" the draggable item into a brand-new row
 *
 * There are special components (VerticalDroppable, HorizontalDroppable) on each edge of the chart that act as
 * droppables and indicators where the item will be moved
 *
 * @param onDrop - a handler invoked when user drops an item. It is only invoked if the item is dropped _in_
 * the grid (never - when outside) and that `canDrop` (see below) allowed dropping at that position
 * @param canDrop - a function which checks if the current dropped item can be dropped at the specified position
 * @param children - <DndGridRow> and <DndGridCell> components
 * @param disabled - if true, drag and drop is disabled
 * @param overlay - overlay component to display while dragging
 * @param borderMenuItemsFactory - optional factory to create menu items on the borders
 */
export const DndGrid = ({
  onDrop,
  canDrop,
  children,
  disabled,
  overlay,
  borderMenuItemsFactory,
}: PropsWithChildren<IDndGridProps>) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const [currentCanDrop, setCurrentCanDrop] = useState<ICanDropOutcome>(
    INITIAL_CAN_DROP_OUTCOME,
  );
  const [currentDropPosition, setCurrentDropPosition] =
    useState<IGridDropPosition | null>(null);
  const [currentDragHandle, setCurrentDragHandle] =
    useState<UniqueIdentifier | null>(null);

  const handleDragMove = (event: DragMoveEvent) => {
    const collision = getClosestCollision(event.collisions);
    if (collision) {
      // since data from the dndkit library is untyped, we use zod to validate
      // otherwise we need to perform casts that may give us a false sense of security
      // the data is provided by our closestEdgeCollisionDetection function
      const collisionDescriptor = CollisionDescriptorSchema.parse(collision);
      const newDropPosition =
        collisionDescriptor.data.droppableContainer.data.current;
      setCurrentDropPosition(newDropPosition);
      const source = GridPositionSchema.parse(event.active.data.current);
      setCurrentCanDrop(canDrop(source, newDropPosition));
    } else {
      setCurrentDropPosition(null);
    }
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (currentDropPosition) {
      const source = GridPositionSchema.parse(event.active.data.current);
      const canDropResult = canDrop(source, currentDropPosition);
      if (canDropResult.canDrop) {
        onDrop(source, currentDropPosition);
      }
      setCurrentDropPosition(null);
    }
    setIsDragging(false);
    setCurrentCanDrop(INITIAL_CAN_DROP_OUTCOME);
    // small UX improvement: by displaying the handle of the currently dropped item, we also highlight the last
    // item we dragged, and user can drag it again if we dropped it outside the grid
    setCurrentDragHandle(event.active.id);
  };

  // use context to pass DnD context (what is currently dragging, can you drop, etc.) to avoid prop-drilling
  // when the subcomponents need to know or modify the DnD state
  const contextValue = useMemo<IDndFlexPrivateContext>(
    () => ({
      currentDropPosition,
      currentDragHandle,
      setCurrentDragHandle,
      currentCanDrop,
      disabled,
      borderMenuItemsFactory,
    }),
    [
      borderMenuItemsFactory,
      currentCanDrop,
      currentDragHandle,
      currentDropPosition,
      disabled,
    ],
  );

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragMove={handleDragMove}
    >
      <DndGridInternalContext.Provider value={contextValue}>
        <DndGridWrapper>{children}</DndGridWrapper>
        {isDragging && (
          <FloatingLayer>
            <DragOverlay>{overlay}</DragOverlay>
          </FloatingLayer>
        )}
      </DndGridInternalContext.Provider>
      <DraggingGlobalStyle $isDragging={isDragging} />
    </DndContext>
  );
};
